/**
 * Web API client — Bearer-token flow.
 *
 * Cross-site cookies (`.pages.dev` → `.workers.dev`) are unreliable:
 * every browser now blocks or partitions third-party cookies to some
 * degree. Rather than fight that, we go token-only: tokens live in
 * localStorage and travel in the `Authorization` header. Identical
 * shape to the mobile client.
 *
 * Trade-off: localStorage is XSS-exposed. Acceptable for a personal
 * finance tool where no third-party scripts run and the CSP on the
 * Pages deployment is tight. If we ever host user-content or accept
 * arbitrary embeds we'll want to revisit.
 */

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';
const ACCESS_KEY = 'rf.access';
const REFRESH_KEY = 'rf.refresh';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  readonly requestId: string;
  constructor(status: number, body: { code: string; message: string; details?: Record<string, unknown>; requestId: string }) {
    super(body.message);
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.requestId = body.requestId;
  }
}

export interface Tokens {
  accessToken: string;
  refreshToken: string;
  refreshExpiresAt: number;
}

export function saveTokens(t: Tokens): void {
  try {
    localStorage.setItem(ACCESS_KEY, t.accessToken);
    localStorage.setItem(REFRESH_KEY, t.refreshToken);
  } catch {
    // Private mode / storage blocked. Requests will fail but the
    // rest of the app degrades to a signed-out state, not a crash.
  }
}
export function clearTokens(): void {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    // no-op
  }
}
export function hasTokens(): boolean {
  try { return !!localStorage.getItem(ACCESS_KEY) || !!localStorage.getItem(REFRESH_KEY); }
  catch { return false; }
}
function getAccess(): string | null {
  try { return localStorage.getItem(ACCESS_KEY); } catch { return null; }
}
function getRefresh(): string | null {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
}

let refreshInFlight: Promise<boolean> | null = null;
async function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const refresh = getRefresh();
      if (!refresh) return false;
      const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) {
        // Refresh has definitively failed — clear so the user is
        // pushed to the login screen instead of loop-refreshing.
        clearTokens();
        return false;
      }
      const body = (await res.json()) as { tokens: Tokens };
      saveTokens(body.tokens);
      return true;
    } finally {
      queueMicrotask(() => (refreshInFlight = null));
    }
  })();
  return refreshInFlight;
}

export interface ApiOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  skipAuthRetry?: boolean;
}

export async function api<T>(path: string, opts: ApiOpts = {}): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_URL}${path}`;
  const send = async (): Promise<Response> => {
    const access = getAccess();
    return fetch(url, {
      method: opts.method ?? 'GET',
      headers: {
        ...(opts.body ? { 'content-type': 'application/json' } : {}),
        ...(access ? { authorization: `Bearer ${access}` } : {}),
      },
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  };

  let res = await send();
  if (res.status === 401 && !opts.skipAuthRetry) {
    if (await refreshOnce()) res = await send();
  }
  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : ({} as unknown);
  if (!res.ok) {
    throw new ApiError(
      res.status,
      parsed as { code: string; message: string; details?: Record<string, unknown>; requestId: string },
    );
  }
  return parsed as T;
}
